<template>
  <div v-if="active">
    <div
        v-for="(item, alr) in filteredItems"
        :key="`alert-${alr}`"
        :data-id="alr"
        :class="getAlertClasses(item)"
        class="alert transition-all"
        :style="`animation-delay: ${animationDelay}s;`"
    >

      <div
          class="container container-wide relative w-full overflow-hidden"
          :class="containerClasses"
      >
        <!-- Mobile Layout - Single row: icon, text (wraps), hide button -->
        <div v-if="mobile" class="flex items-center gap-3 w-full">
          <AlertIcon :item="item" size="small" class="flex-shrink-0" />

          <div class="flex-grow min-w-0">
            <div class="font-light text-dark text-sm leading-[22px]">
              <span v-if="item.add_notice_text" class="font-bold pr-1">NOTICE: </span>
              <span v-html="item.alert_message" class="alert-message"></span>
            </div>
          </div>

          <CloseButton @close="closeAlert(item, alr)" size="small" class="flex-shrink-0" />
        </div>

        <!-- Desktop Layout -->
        <template v-else>
          <div class="flex items-center gap-3 flex-grow min-w-0">
            <AlertIcon :item="item" size="large" />

            <div class="flex items-center gap-4 flex-grow min-w-0">
              <div class="font-light text-dark leading-4 text-sm lg:text-lg">
                <span v-if="item.add_notice_text" class="font-bold pr-1">NOTICE: </span>
                <span v-html="item.alert_message" class="alert-message"></span>
              </div>
            </div>
          </div>

          <CloseButton @close="closeAlert(item, alr)" size="large" />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import ResponsiveMixin from "./../../mixins/responsive"
import AlertIcon from "./AlertIcon.vue";
import CloseButton from "./CloseButton.vue";

// Constants
const STORAGE_KEYS = {
  ALERT: 'alert',
  ALERT_VALUE: 'alert_value',
  ALERT_EXPIRED: 'alert_expired_at',
  PAGE_COUNTER: 'pageCounter'
}

const DISPLAY_OPTIONS = {
  EVERYTIME: 'everytime',
  ONCE_PER_SESSION: 'once_per_session',
  PER_DAYS: 'per_days'
}

const SHOW_OPTIONS = {
  TIME: 'time',
  SESSION: 'session'
}

const DAY_IN_MS = 1000 * 60 * 60 * 24
const DEFAULT_DAYS = 2


const BACKGROUND_CONFIG = {
  'light': 'bg-light',
  'alert_error_light': 'bg-alert-error-light',
  'forest_primary': 'bg-forest-primary',
  'caves_primary': 'bg-caves-primary',
  'lighthouse_primary': 'bg-lighthouse-primary',
  'capes_primary': 'bg-capes-primary',
  'default': 'bg-light'
}

const BORDER_CONFIG = {
  'default': 'border-t-default',
  'alert_error': 'border-t-alert-error',
  'forest_secondary': 'border-t-forest-secondary',
  'caves_secondary': 'border-t-caves-secondary',
  'lighthouse_secondary': 'border-t-lighthouse-secondary',
  'capes_secondary': 'border-t-capes-secondary'
}



export default {
  mixins: [ResponsiveMixin],

  components: {
    AlertIcon,
    CloseButton,
  },

  data() {
    return {
      active: false,
      activeAlerts: 0,
      animationDelay: 0,
      items: []
    }
  },

  computed: {
    filteredItems() {
      return this.items.filter(item => item.isShown)
    },

    containerClasses() {
      return this.mobile
          ? 'flex items-center py-3 px-4 xl:px-8'
          : 'flex items-center h-[74px] py-1 lg:py-2 px-4 xl:px-8 justify-between'
    }
  },

  created() {
    this.build()
  },

  methods: {
    // Configuration helpers
    getThemeConfig(item) {
      return {
        bgClass: BACKGROUND_CONFIG[item.background_colour] || BACKGROUND_CONFIG.default,
        borderClass: BORDER_CONFIG[item.border_colour] || BORDER_CONFIG.default,
      }
    },

    getAlertClasses(item) {
      const config = this.getThemeConfig(item)
      return `${config.bgClass} ${config.borderClass} border-t-[3px]`
    },

    // Display options helpers
    getDisplayOptions(item) {
      return {
        whenToDisplay: item?.display_options?.when_to_display || DISPLAY_OPTIONS.EVERYTIME,
        showOption: item?.display_options?.show_options ?? null,
        showAfterSeconds: item?.display_options?.show_after_x_seconds ?? 0,
        showAfterPages: item?.display_options?.show_after_x_pages_have_been_viewed ?? 0,
        numberOfDays: item?.display_options?.number_of_days ?? DEFAULT_DAYS,
        bannerLocation: item?.display_options?.show_banner_location ?? null
      }
    },

    // Storage management
    getSessionClosedAlerts() {
      return Object.keys(sessionStorage)
          .filter(key => key.startsWith(STORAGE_KEYS.ALERT))
          .map(key => sessionStorage.getItem(key))
    },

    getLocalClosedAlerts() {
      this.clearExpiredLocalAlerts()

      return Object.keys(localStorage)
          .filter(key => key.startsWith(STORAGE_KEYS.ALERT_VALUE))
          .map(key => {
            const id = key.replace(`${STORAGE_KEYS.ALERT_VALUE}_`, '')
            return {
              id,
              expired_at: localStorage.getItem(`${STORAGE_KEYS.ALERT_EXPIRED}_${id}`),
              hash: localStorage.getItem(key)
            }
          })
    },

    clearExpiredLocalAlerts() {
      const now = Date.now()
      const thirtyDaysInMs = DAY_IN_MS * 30

      Object.keys(localStorage)
          .filter(key => key.startsWith(STORAGE_KEYS.ALERT_EXPIRED))
          .forEach(key => {
            const id = key.replace(`${STORAGE_KEYS.ALERT_EXPIRED}_`, '')
            const expiredAt = parseInt(localStorage.getItem(key))

            if (expiredAt > now + thirtyDaysInMs) {
              localStorage.removeItem(`${STORAGE_KEYS.ALERT_EXPIRED}_${id}`)
              localStorage.removeItem(`${STORAGE_KEYS.ALERT_VALUE}_${id}`)
            }
          })
    },

    // Alert visibility logic
    isWithinDisplayWindow(item) {
      const now = Date.now()

      // PHP supplies ISO 8601 dates with the WordPress site's timezone offset.
      return (!item.display_from || now >= Date.parse(item.display_from))
          && (!item.display_until || now <= Date.parse(item.display_until))
    },

    shouldShowAlert(item, sessionClosedList, localClosedList) {
      if (!item.enabled || !this.isWithinDisplayWindow(item)) return false

      const { whenToDisplay } = this.getDisplayOptions(item)
      const hash = item.hash

      switch (whenToDisplay) {
        case DISPLAY_OPTIONS.EVERYTIME:
        case DISPLAY_OPTIONS.ONCE_PER_SESSION:
          return !sessionClosedList.includes(hash)

        case DISPLAY_OPTIONS.PER_DAYS:
          if (localClosedList.length === 0) return true

          const now = Date.now()
          const closedAlert = localClosedList.find(alert =>
              alert.hash.includes(hash) && now < parseInt(alert.expired_at)
          )
          return !closedAlert

        default:
          return false
      }
    },

    applyDisplayRules(item) {
      const { showOption, showAfterSeconds, showAfterPages } = this.getDisplayOptions(item)

      if (showOption === SHOW_OPTIONS.TIME) {
        this.animationDelay = parseInt(showAfterSeconds)
        return true
      }

      if (showOption === SHOW_OPTIONS.SESSION) {
        const pageCounter = this.incrementPageCounter()
        return pageCounter >= parseInt(showAfterPages)
      }

      return true
    },

    incrementPageCounter() {
      let pageCounter = parseInt(sessionStorage.getItem(STORAGE_KEYS.PAGE_COUNTER)) || 0
      pageCounter++
      sessionStorage.setItem(STORAGE_KEYS.PAGE_COUNTER, pageCounter)
      return pageCounter
    },

    // Build alerts
    build() {
      const items = window?.capes?.alerts ?? []
      if (!items || items.length === 0) return

      const sessionClosedList = this.getSessionClosedAlerts()
      const localClosedList = this.getLocalClosedAlerts()

      this.items = items.map(item => {
        item.isShown = false

        if (this.shouldShowAlert(item, sessionClosedList, localClosedList)) {
          item.isShown = this.applyDisplayRules(item)
        }

        if (item.isShown) {
          this.active = true
          this.activeAlerts++
        }

        return item
      })

      if (this.active) {
        document.querySelector("#app")?.classList.add("has-alert");
      }

      setTimeout(() => {
        const alertMessages = document.querySelectorAll('.alert-message a');

        [...alertMessages].forEach((messageLink) => {
          messageLink.addEventListener('click', () => {
            if (window?.fathom) {
              window.fathom.trackEvent('Alert ' + messageLink.textContent);
            }
          })
        })
      }, 1000)
    },

    // Close alert
    closeAlert(item, filteredIndex) {
      this.activeAlerts--

      const el = document.querySelector(`.alert[data-id='${filteredIndex}']`)
      el?.classList.add("is-hidden")

      const originalIndex = this.items.indexOf(item)
      if (originalIndex === -1) {
        this.checkAndDeactivate()
        return
      }

      const { whenToDisplay, numberOfDays } = this.getDisplayOptions(this.items[originalIndex])
      const alertKey = Math.random()
      const alertHash = this.items[originalIndex].hash

      if ([DISPLAY_OPTIONS.ONCE_PER_SESSION, DISPLAY_OPTIONS.EVERYTIME].includes(whenToDisplay)) {
        sessionStorage.setItem(`${STORAGE_KEYS.ALERT}_${alertKey}`, alertHash)
      } else if (whenToDisplay === DISPLAY_OPTIONS.PER_DAYS) {
        this.saveAlertToLocalStorage(alertKey, alertHash, numberOfDays)
      }

      this.checkAndDeactivate()
    },

    saveAlertToLocalStorage(key, hash, days) {
      const expiredAt = Date.now() + (days * DAY_IN_MS)

      // Remove old entries with same hash
      Object.keys(localStorage)
          .filter(storageKey => localStorage.getItem(storageKey) === hash)
          .forEach(storageKey => {
            const id = storageKey.replace(`${STORAGE_KEYS.ALERT_VALUE}_`, '')
            localStorage.removeItem(`${STORAGE_KEYS.ALERT_EXPIRED}_${id}`)
            localStorage.removeItem(storageKey)
          })

      localStorage.setItem(`${STORAGE_KEYS.ALERT_VALUE}_${key}`, hash)
      localStorage.setItem(`${STORAGE_KEYS.ALERT_EXPIRED}_${key}`, expiredAt)
    },

    checkAndDeactivate() {
      if (this.activeAlerts <= 0) {
        document.querySelector("#app")?.classList.remove("has-alert")
        this.active = false
      }
    }
  }
}
</script>

<style scoped>
.alert {
  overflow: hidden;
  opacity: 0;
  height: 0;
  animation: 0.5s fadeIn forwards;
}

.alert.is-hidden {
  animation: fadeOut 0.5s forwards;
}

.alert :deep(p) {
  display: inline;
  margin: 0;
  padding: 0;
}

.alert :deep(a:not(.button):not(.alert-button)) {
  @apply font-semibold no-underline hover:underline;
  color: inherit;
}

.alert :deep(a:not(.button):not(.close-button)) {
  @apply hover:no-underline;
  color: inherit;
  text-decoration: underline;
}

.alert :deep(a.underline) {
  @apply hover:no-underline;
  color: inherit;
  text-decoration: underline;
}


.alert :deep(.button) {
  @apply no-underline;
}

@media screen and (max-width: 768px) {
  .button-primary {
    @apply inline-flex leading-none;
  }
}

@screen md {
  .alert {
    animation: 0.5s fadeInMd forwards;
  }
}

@screen lg {
  .alert {
    animation: 0.5s fadeInLg forwards;
  }
}

@keyframes fadeIn {
  0% {
    opacity: 0;
    height: auto;
    max-height: 0;
  }
  20% {
    height: auto;
    max-height: 200px;
    min-height: 56px;
  }
  100% {
    opacity: 1;
    height: auto;
    max-height: 200px;
    min-height: 56px;
  }
}

@keyframes fadeInMd {
  0% {
    opacity: 0;
    height: 0;
  }
  20% {
    height: 74px;
  }
  100% {
    opacity: 1;
    height: 74px;
  }
}

@keyframes fadeInLg {
  0% {
    opacity: 0;
    height: 0;
  }
  20% {
    height: 74px;
  }
  100% {
    opacity: 1;
    height: 74px;
  }
}

@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    height: 0;
  }
}
</style>
